/**
 * The contract of POST /v1/net-metering/calculation on capi (#6322). Snake case, because that is
 * what the API speaks.
 */
export type Amount = {
  annual: number
  monthly: number
}

/**
 * A missing tariff comes back as PRICES_UNAVAILABLE with a message and a 200, not as an error
 * status code, so the tool can show a readable message instead of an error screen. The amounts are
 * only present when the status is CALCULATED. Uppercase: these are the CalculationStatus enum
 * names capi serialises.
 */
export type CalculationResult = {
  status: 'CALCULATED' | 'PRICES_UNAVAILABLE'
  with_netting?: Amount
  without_netting?: Amount
  difference?: Amount
  message?: string
}

/**
 * The contract options in the tool. The values are the Duration case names that
 * CalculationRequestData::CONTRACT_TYPES in capi accepts (#6323), plus UNKNOWN, which capi
 * calculates as a one year fixed contract.
 */
export type ContractType = 'DYNAMIC' | 'ONE_YEAR' | 'VARIABLE' | 'HYBRID' | 'UNKNOWN'

/**
 * Only a fixed contract runs to a date; the other types have none to ask for. Empty counts as
 * applicable: nothing is chosen yet, so the date field stays open.
 */
export function contractHasEndDate(type: ContractType | ''): boolean {
  return type === '' || type === 'ONE_YEAR'
}

/** Labels per design: shorter than the ones in the customer portal and the choice engine. */
export const CONTRACT_TYPES: { value: ContractType; label: string }[] = [
  { value: 'DYNAMIC', label: 'Dynamisch' },
  { value: 'ONE_YEAR', label: 'Vast' },
  { value: 'VARIABLE', label: 'Variabel' },
  { value: 'HYBRID', label: 'Hybride' },
  { value: 'UNKNOWN', label: 'Weet ik niet' },
]

/**
 * What a chosen option means for the calculation, standing under the select. The closed select
 * holds one line that cannot wrap, so a note like this does not fit in the option label itself.
 */
export const CONTRACT_TYPE_HINTS: Partial<Record<ContractType, string>> = {
  UNKNOWN: 'We rekenen met de tarieven voor 1 jaar vast.',
}

/**
 * What the visitor fills in. capi validates it and owns every message, so an empty field travels
 * as null instead of as a zero it would happily calculate with.
 *
 * The end date does not reach the calculation itself; it rides along so its message comes from the
 * same place as the others.
 */
export type CalculationInput = {
  electricity_usage: number | null
  generation: number | null
  has_gas_connection: boolean
  gas_usage: number | null
  contract_type: ContractType | ''
  end_date: string | null
  contract_end_unknown: boolean
}

/** The action plan request on capi (#6325): the calculation input plus contact details. */
export type ActionPlanInput = CalculationInput & {
  email: string
  newsletter_consent: boolean
}

/**
 * Where the tool is, resolved down to one value so a screen never has to ask a follow up question.
 * Null until the visitor submits something.
 *
 * calculating  the request is running
 * calculated   an answer with amounts
 * unavailable  a valid answer without amounts, a tariff is missing
 * invalid      capi rejected the input, see fieldErrors
 * throttled    too many calculations from this visitor, waiting is the only way forward
 * failed       the request itself did not make it, no usable message
 */
export type CalculationStatus = 'calculating' | 'calculated' | 'unavailable' | 'invalid' | 'throttled' | 'failed'
